import React, {
  createContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { max, min } from "lodash";
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { useCustomerBreakdownConfigs } from "@components/data/customer-breakdown-configs";
import { type Sorting } from "@features/breakdown-v2/breakdowns/components/edit-breakdown/advanced-editor/types";
import { GET_CUSTOMER_REPORT_V2 } from "@gql/customer";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type GetCustomerReportV2,
  type GetCustomerReportV2Variables,
  type GetCustomerReportV2_me_customerReportV2_max,
  type GetCustomerReportV2_me_customerReportV2_min,
  type GetCustomerReportV2_me_customerReportV2_rows,
} from "@north-beam/nb-client-side";
import { CustomJsonParam } from "@north-beam/nb-common";
import { DEFAULT_BOUNDS, ROLLUP_MODES } from "@pages/customers/report";
import {
  defaultSumMode,
  type SumMode,
} from "@pages/customers/report/sum-mode-select";

import { DEFAULT_SORT_BY, METRIC_OPTIONS, ROWS_PER_PAGE } from "./constants";

interface FirstAcquiredMonthRange {
  startMonth: string;
  endMonth: string;
}

interface Search {
  operator: string;
  query: string;
}

interface Filters {
  breakdown: { key: string; values: string[] } | null;
  firstAcquiredMonthRange: FirstAcquiredMonthRange;
  metric: string;
  sumMode: SumMode;
  rollupMode: string;
  search: Search;
  page: number;
  sort: Sorting;
}

export interface CustomerLtvV2Data {
  summaryRow: GetCustomerReportV2_me_customerReportV2_rows;
  rows: GetCustomerReportV2_me_customerReportV2_rows[];
  minValues: GetCustomerReportV2_me_customerReportV2_min | null;
  maxValues: GetCustomerReportV2_me_customerReportV2_max | null;
  totalRows: number;
}

interface CustomerLtvV2 {
  loading: boolean;
  data: CustomerLtvV2Data | null;
  filters: Filters;
  firstAcquiredBounds: FirstAcquiredMonthRange;
  setFilters: (filters: Partial<Filters>) => void;
}

export const CustomerLtvV2Context = createContext<CustomerLtvV2 | null>(null);

const BreakdownParam = CustomJsonParam<Filters["breakdown"]>();
const FirstAcquiredMonthRangeParam =
  CustomJsonParam<Filters["firstAcquiredMonthRange"]>();
const SortingParam = CustomJsonParam<Sorting>();
const SearchParam = CustomJsonParam<Search>();

export const CustomerLtvV2Provider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { value: breakdownConfigs } = useCustomerBreakdownConfigs();

  const firstAcquiredBounds = useMemo(() => {
    const config = breakdownConfigs?.find((v) => v.key === "first_order_month");

    if (!config) {
      return DEFAULT_BOUNDS;
    }

    const months = config.choices
      .map((e) => e.value)
      /* eslint-disable-next-line quotes  */
      .map((e) => e.replaceAll('"', ""));

    const minMonth = min(months);
    const maxMonth = max(months);

    if (!minMonth || !maxMonth) {
      return DEFAULT_BOUNDS;
    } else {
      return { startMonth: minMonth, endMonth: maxMonth };
    }
  }, [breakdownConfigs]);

  const [filters, setFilters] = useQueryParams({
    breakdown: withDefault(BreakdownParam, null),
    firstAcquiredMonthRange: withDefault(
      FirstAcquiredMonthRangeParam,
      firstAcquiredBounds,
    ),
    metric: withDefault(StringParam, Object.keys(METRIC_OPTIONS)[0]),
    sumMode: withDefault(StringParam, defaultSumMode),
    rollupMode: withDefault(StringParam, ROLLUP_MODES[1]),
    search: withDefault(SearchParam, { operator: "contains", query: "" }),
    page: withDefault(NumberParam, 1),
    sort: withDefault(SortingParam, { column: DEFAULT_SORT_BY, order: "desc" }),
  });

  const [data, setData] = useState<CustomerLtvV2["data"]>(null);

  const skipLoad = !filters.breakdown;

  const { loading: rowsLoading, data: rowsData } = useNorthbeamQuery<
    GetCustomerReportV2,
    GetCustomerReportV2Variables
  >(GET_CUSTOMER_REPORT_V2, {
    variables: {
      dateRange: filters.firstAcquiredMonthRange,
      breakdown: filters.breakdown?.key ?? "",
      metric: filters.metric,
      rollupMode: filters.rollupMode,
      sumMode: filters.sumMode,
      aggregateAll: false,
      searchOperator: filters.search.operator,
      searchQuery: filters.search.query,
      page: filters.page,
      perPage: ROWS_PER_PAGE,
      sort: filters.sort,
    },
    skip: skipLoad,
  });

  const { loading: aggLoading, data: aggData } = useNorthbeamQuery<
    GetCustomerReportV2,
    GetCustomerReportV2Variables
  >(GET_CUSTOMER_REPORT_V2, {
    variables: {
      dateRange: filters.firstAcquiredMonthRange,
      breakdown: filters.breakdown?.key ?? "",
      metric: filters.metric,
      rollupMode: filters.rollupMode,
      sumMode: filters.sumMode,
      aggregateAll: true,
      searchOperator: filters.search.operator,
      searchQuery: filters.search.query,
    },
    skip: skipLoad,
  });

  useEffect(() => {
    if (aggData && rowsData) {
      const summary = aggData.me.customerReportV2;
      const report = rowsData.me.customerReportV2;

      setData({
        summaryRow: summary.rows[0],
        rows: report.rows,
        minValues: report.min,
        maxValues: report.max,
        totalRows: report.total,
      });
    }
  }, [aggData, rowsData]);

  const loading = rowsLoading || aggLoading;

  return (
    <CustomerLtvV2Context.Provider
      value={{
        loading,
        data,
        filters: { ...filters, sumMode: filters.sumMode as SumMode },
        firstAcquiredBounds,
        setFilters,
      }}
    >
      {children}
    </CustomerLtvV2Context.Provider>
  );
};
